import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { getIn, useFormikContext } from 'formik';
import { Grid, Button } from '@material-ui/core';
import { FarmRiskType } from '@ourbranch/lookups';

import Label from 'core/components/label/label';
import AddCircleIcon from 'core/assets/svg/ic-add.svg?react';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import { useStore } from 'core/store';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';
import { FO6Scheduled } from '../risks/risk-types/FO6-scheduled';
import { FO6Unscheduled } from '../risks/risk-types/FO6-unscheduled';
import { useActiveFormState } from 'common/hooks/use-active-form-state';

import useStyles from './farming-equipment.styles';

const defaultScheduledFarmPropertyRisk = {
  type: FarmRiskType.Fo6Scheduled,
  description: 'Scheduled farm property',
  adjustmentFactor: 0,
  coverages: {
    coverageF: [],
    expandedDebrisRemoval: false,
    hasSnowWeightCoverage: false,
    deductibleEarthquake: 0.15
  }
};

const defaultUnscheduledFarmPropertyRisk = {
  type: FarmRiskType.Fo6Unscheduled,
  description: 'Unscheduled farm property',
  adjustmentFactor: 0,
  coverages: {
    coverageG: 0,
    deductibleAllOther: 1500,
    deductibleEarthquake: 0.05,
    deductibleWindHail: 5000,
    lossSettlement: 'ACV',
    coinsurance: 0.8
  }
};

const FarmingEquipment = observer(({ fieldName }) => {
  const classes = useStyles();
  const { values, setFieldValue, validateForm, setErrors, setTouched } = useFormikContext();
  const { disableForm, setActiveForm } = useActiveFormState(fieldName);
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const {
    offer: { selectedOption, state: offerState }
  } = useStore();

  const risks = useMemo(() => getIn(values, `${fieldName}.risks`) || [], [values, fieldName]);

  const fo6Scheduled = risks.find((risk) => risk.type === 'FO6Scheduled');
  const fo6Unscheduled = risks.find((risk) => risk.type === 'FO6Unscheduled');

  // Calculate predictable risk indexes
  const fo6ScheduledIndex = risks.findIndex((risk) => risk.type === 'FO6Scheduled');
  const fo6UnscheduledIndex = risks.findIndex((risk) => risk.type === 'FO6Unscheduled');

  const total = Number(!!fo6Scheduled) + Number(!!fo6Unscheduled);

  // Policy-type mask gating: Home (Z) and Rental (Y) don't allow scheduled/unscheduled farm
  // property, so don't offer the add buttons for disallowed types. Existing risks (e.g. created
  // before the mask) still render so data is never hidden. GitHub issues #31693 / #31694.
  const locationState = getIn(values, `${fieldName}.address.state`) || offerState;
  const mask = getFarmPolicyMask(selectedOption, locationState);
  const allowedRiskTypes = mask?.allowedRiskTypes;
  const scheduledAllowed = !allowedRiskTypes || allowedRiskTypes.includes(FarmRiskType.Fo6Scheduled);
  const unscheduledAllowed = !allowedRiskTypes || allowedRiskTypes.includes(FarmRiskType.Fo6Unscheduled);
  const showScheduled = Boolean(fo6Scheduled) || scheduledAllowed;
  const showUnscheduled = Boolean(fo6Unscheduled) || unscheduledAllowed;

  if (!showScheduled && !showUnscheduled) {
    return null;
  }

  const handleAddProperty = async ({ risks, riskType }) => {
    // Validate the parent form before allowing the add
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      setTouched(setTouchedFromErrors(errors), false);
      setShowValidationErrorsModal(true);
      return;
    }

    const newRisks = [
      ...risks,
      {
        ...(riskType === 'FO6Scheduled' ? defaultScheduledFarmPropertyRisk : defaultUnscheduledFarmPropertyRisk)
      }
    ];
    setFieldValue(`${fieldName}.risks`, newRisks);
    const newRiskIndex = newRisks.length - 1;

    setActiveForm(`${fieldName}.risks[${newRiskIndex}]`);
  };

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <Grid container className={classes.mainContainer}>
        <Grid container justifyContent="space-between" className={classes.titleContainer}>
          <Label type="sectionSubtitle">Farming scheduled items</Label>
          <Grid item className={classes.rightContainer}>
            <Label type="body4" className={classes.rightLabel}>
              Total
            </Label>
            <Label type="body4">{total}</Label>
          </Grid>
        </Grid>
        <Grid container>
          {fo6Scheduled && <FO6Scheduled fieldName={fieldName} riskIndex={fo6ScheduledIndex} />}
          {!fo6Scheduled && scheduledAllowed && (
            <Grid className={classes.addContainer}>
              <p className={classes.addLabel}>Add scheduled farm property</p>
              <Button
                className={classes.addButton}
                onClick={() => handleAddProperty({ risks, riskType: 'FO6Scheduled' })}
                aria-label="Add scheduled farm property"
                disabled={disableForm}
              >
                <AddCircleIcon />
              </Button>
            </Grid>
          )}
          {fo6Unscheduled && <FO6Unscheduled fieldName={fieldName} riskIndex={fo6UnscheduledIndex} />}
          {!fo6Unscheduled && unscheduledAllowed && (
            <Grid className={classes.addContainer}>
              <p className={classes.addLabel}>Add unscheduled farm property</p>
              <Button
                className={classes.addButton}
                onClick={() => handleAddProperty({ risks, riskType: 'FO6Unscheduled' })}
                aria-label="Add unscheduled farm property"
                disabled={disableForm}
              >
                <AddCircleIcon />
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
});

FarmingEquipment.propTypes = {
  fieldName: PropTypes.string.isRequired
};

export default FarmingEquipment;
